import * as companyService from "../../../services/company";
import * as originPackageService from "../../../services/originPackage";
import * as originPackageTransportationService from "../../../services/originPackageTransportation";

import { Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Table, notification } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { formatNumber } from "../../../utils/number";
import { isEmpty } from "../../../utils/common";
import permissions from "../../../../configs/permission";
import { useAccessToken } from "../../../contexts/AccessTokenContext";
import { usePermission } from "../../../hooks/usePermission";
import { useTranslation } from "react-i18next";

const ModalAddPackageToContainer = ({ open, setOpen, data, fetchOriginPackageTransportationData, route, originId }) => {
  const { hasPermission, isSuperuser } = usePermission();
  const [notiApi, contextHolder] = notification.useNotification();
  const { t: tBtn } = useTranslation("common");
  const { t } = useTranslation("originpackagetransportation", {
    keyPrefix: "Origin_Package_Transportation_Create.Add_Package_Modal",
  });
  const { t: tOps } = useTranslation("options", { keyPrefix: "Package" });
  const { accessToken } = useAccessToken();

  const [customer, setCustomer] = useState({ loading: false, data: [] });
  const [tableData, setTableData] = useState({
    loading: false,
    datasource: [],
    selectedRowKeys: [],
    pagination: { current: 1, pageSize: 10 },
    total: 0,
  });

  const [submitting, setSubmitting] = useState(false);
  const hasFetchedCustomer = useRef(false);

  const [form] = Form.useForm();

  const onSearch = useCallback(
    async (options = {}) => {
      if (!originId) return;
      try {
        const values = form.getFieldsValue();
        const page = options.page ?? tableData.pagination.current;
        const limit = options.pageSize ?? tableData.pagination.pageSize;
        const params = {
          page,
          limit,
          values: {
            start_date: values.date ? values.date[0].format("YYYY-MM-DD") : undefined,
            end_date: values.date ? values.date[1].format("YYYY-MM-DD") : undefined,
            reference: values.search,
            tracking: values.search,
            package_description: values.search,
            customer: values.customer,
            origin: originId,
            // route: route,
          },
        };

        setTableData((p) => ({ ...p, loading: true }));
        const resp = await originPackageService.getOriginPackageContainer(accessToken, params);
        const datasource = resp.queryOriginPackage.map((item) => ({
          ...item,
          input_total_received: item.total_received,
        }));
        setTableData((p) => ({
          ...p,
          loading: false,
          selectedRowKeys: [],
          datasource: datasource,
          total: resp.totalCount,
        }));
      } catch (err) {
        console.error("[ModalAddPackageToContainer] onSearch: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        setTableData((p) => ({ ...p, loading: false }));
      }
    },
    [accessToken, form, tableData.pagination.current, tableData.pagination.pageSize, originId]
  );

  useEffect(() => {
    if (open && originId) {
      onSearch();
    }
  }, [open, originId, onSearch]);

  useEffect(() => {
    if (!accessToken || hasFetchedCustomer.current) return;
    hasFetchedCustomer.current = true;
    const fetchCustomer = async () => {
      try {
        setCustomer((p) => ({ ...p, loading: true }));
        const data = await companyService.getCompanysList(accessToken);
        setCustomer((p) => ({ ...p, loading: false, data }));
      } catch (err) {
        console.error("[ModalAddPackageToContainer] fetchCustomer: ", err);
        hasFetchedCustomer.current = false;
        setCustomer((p) => ({ ...p, loading: false }));
      }
    };
    fetchCustomer();
  }, [accessToken]);

  const onClose = () => {
    setOpen(false);
  };

  const customerOptions = useMemo(() => {
    const options = [{ label: tOps("All"), value: "all" }];
    if (!isEmpty(customer.data)) {
      customer.data.map((value) => {
        value.app_sale.map((saleValue) => {
          saleValue.app_customer.map((customerValue) => {
            options.push({
              label: `${value.company_code}-${saleValue.sale_code}${customerValue.customer_code}`,
              value: customerValue.id,
            });
          });
        });
      });
    }
    return options;
  }, [customer.data]);

  const columns = [
    {
      title: t("Table_Total_Received"),
      dataIndex: "input_total_received",
      width: 150,
      render: (value, rec) => {
        return (
          <InputNumber
            value={value}
            min={0}
            max={rec.total_received}
            onChange={(value) => {
              const newData = { ...rec, input_total_received: value ?? 0 };
              const datasource = tableData.datasource.map((item) => (item.id === rec.id ? newData : item));
              setTableData((prev) => ({ ...prev, datasource }));
            }}
          />
        );
      },
    },
    {
      title: t("Table_Reference"),
      dataIndex: "reference",
      width: 150,
    },
    {
      title: t("Table_Box"),
      dataIndex: "box",
      width: 165,
    },
    {
      title: t("Table_Tracking"),
      dataIndex: "tracking",
      width: 150,
    },
    {
      title: t("Table_Custom_Number"),
      dataIndex: "custom_no",
      width: 150,
    },
    {
      title: t("Table_Warehouse"),
      width: 150,
      render: (_, record) => {
        const warehouse = record.app_warehouse_app_originpackage_origin_idToapp_warehouse
          ? record.app_warehouse_app_originpackage_origin_idToapp_warehouse.warehouse_code
          : "-";
        return <p>{warehouse}</p>;
      },
    },
    {
      title: t("Table_Description"),
      dataIndex: "package_description",
    },
    {
      title: t("Table_Type"),
      dataIndex: "package_type",
      width: 100,
    },
    {
      title: t("Table_Length"),
      dataIndex: "length",
      width: 100,
    },
    {
      title: t("Table_Width"),
      dataIndex: "width",
      width: 100,
    },
    {
      title: t("Table_Height"),
      dataIndex: "height",
      width: 100,
    },
    {
      title: t("Table_Density"),
      dataIndex: "density",
      width: 100,
      render: (value) => formatNumber(value, 4, "round"),
    },
  ];

  const onAddPackage = async () => {
    try {
      const selectedRow = tableData.datasource.filter((item) => tableData.selectedRowKeys.includes(item.id));
      setSubmitting(true);
      let success = 0;
      let failed = 0;
      let responseSuccess = [];
      let responseFailed = [];
      let responseMessage = "";
      let errorCode = [204, 400, 403, 500];
      for (let i = 0; i < selectedRow.length; i++) {
        const dataRow = selectedRow[i];
        const payload = {
          origin_package_id: dataRow.id,
          total_received: dataRow.input_total_received,
          origin_transportation_id: data.id,
          route: route,
          origin_id: originId,
        };
        const resp = await originPackageTransportationService.createOriginPackageTransportation(accessToken, payload);
        if (resp === null || resp === undefined || errorCode.includes(resp.status)) {
          failed += 1;
          responseFailed.push(dataRow.reference);
          responseMessage = resp.response.data.message;
        } else {
          success += 1;
          responseSuccess.push(dataRow.reference);
        }
      }
      if (success > 0) {
        notiApi.success({
          message: t("Modal_Message.Success_Message"),
          description: t("Modal_Message.Success_Description"),
        });
        setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
        setSubmitting(false);
        fetchOriginPackageTransportationData();
        onSearch({ page: 1 });
      }
      if (failed > 0) {
        setSubmitting(false);
        const lowerCaseMsg = typeof responseMessage === "string" ? responseMessage.trim().toLowerCase() : "";
        const errorDescription =
          lowerCaseMsg === "route not match"
            ? t("Modal_Message.Error.Add_Route_Match_Error")
            : lowerCaseMsg === "origin not match"
              ? t("Modal_Message.Error.Add_Origin_Match_Error")
              : responseMessage;
        notiApi.error({
          message: t("Modal_Message.Error_Message"),
          description: t("Modal_Message.Error_Description_With_Failed", {
            errorText: errorDescription,
            failedList: responseFailed.join(", "),
          }),
        });
        setTimeout(() => {
          setSubmitting(false);
        }, 5000);
      }
      setSubmitting(false);
    } catch (err) {
      console.error("[ModalAddPackageToContainer] onAddPackage: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setSubmitting(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal open={open} onCancel={onClose} footer={null} title={t("Title")} width={"85%"} destroyOnClose>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <div className="font-semibold">
              {t("Container")} : {data?.container}
            </div>
          </Col>
          {/* Search */}
          <Col span={24}>
            <div className="border border-gray-200 rounded-md bg-white p-4 shadow-sm">
              <div className="font-semibold text-base mb-4">{t("Search_Title")}</div>
              <Form form={form} layout="vertical">
                <Row gutter={[16]}>
                  <Col span={8}>
                    <Form.Item label={t("Search_Label_Create_Date")} name={"date"}>
                      <DatePicker.RangePicker format={"YYYY-MM-DD"} className="w-full" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={t("Search_Label_Customer")} name={"customer"} initialValue={"all"}>
                      <Select
                        options={customerOptions}
                        loading={customer.loading}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={t("Search_Label_Search")} name={"search"}>
                      <Input
                        placeholder="Reference / Tracking / Description"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
                            onSearch({ page: 1 });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
                        onSearch({ page: 1 });
                      }}
                    >
                      {tBtn("Search")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </Col>
          <Col span={24}>
            <div className="border border-gray-200 rounded-md bg-white shadow-sm p-4 pt-5">
              <Row gutter={[0, 8]}>
                {hasPermission(permissions.OriginPackageTransportation.ADD) && (
                  <Col span={24}>
                    <Button
                      type="primary"
                      disabled={isEmpty(tableData.selectedRowKeys)}
                      loading={submitting}
                      onClick={onAddPackage}
                    >
                      {t("BTN_Add_Package")}
                    </Button>
                  </Col>
                )}
                <Col span={24}>
                  <Table
                    rowKey={"id"}
                    loading={tableData.loading}
                    dataSource={tableData.datasource}
                    columns={columns}
                    rowSelection={{
                      selectedRowKeys: tableData.selectedRowKeys,
                      onChange: (selectedRowKeys) => setTableData((prev) => ({ ...prev, selectedRowKeys })),
                    }}
                    pagination={{
                      current: tableData.pagination.current,
                      pageSize: tableData.pagination.pageSize,
                      showSizeChanger: false,
                      total: tableData.total,
                      onChange: (current, pageSize) =>
                        setTableData((prev) => ({
                          ...prev,
                          pagination: { ...prev.pagination, current, pageSize },
                        })),
                    }}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ModalAddPackageToContainer;
